import { useEffect, useRef, useState } from 'react';
import type { WbsItemDoc, WbsItemKind } from '@mct/shared';
import { cn } from '../../lib/utils';
import { Badge } from '../ui/Badge';
import {
  NESTED_TO_PHASE_MESSAGE,
  getKindChangeWarning,
  validateKindChange,
} from '../../lib/wbsKindGuard';

/**
 * Header chip for a WBS row that doubles as the `task ⇄ phase`
 * switcher — same visual as the original badge, but clickable when
 * (and only when) the existing `wbsKindGuard` rules allow a change.
 *
 * Behaviour rules:
 *   - `milestone` rows render a plain badge (no toggle UI). Milestone
 *     creation is a separate flow; the panel never offered milestone
 *     ↔ task / phase switching.
 *   - Otherwise the chip is rendered as a `<button>`. If switching to
 *     the *other* kind would violate {@link validateKindChange} (i.e.
 *     the only alternative is forbidden), the button is disabled and
 *     the violation message is exposed as the `title` tooltip — same
 *     copy as the original `KindSection` so existing user expectations
 *     carry over.
 *   - Clicking the enabled chip opens a small popover with `タスク` /
 *     `フェーズ`. Selecting the same kind is a no-op; selecting the
 *     other kind re-runs the validator and the soft-warning prompt
 *     (`getKindChangeWarning`) before invoking `onChange`.
 *
 * Closes on outside-click, Esc, or after a successful selection. The
 * caller owns the network write — this component is purely the UI
 * gate.
 */
export function KindChip({
  item,
  allItems,
  onChange,
}: {
  item: WbsItemDoc;
  allItems: readonly WbsItemDoc[];
  onChange: (next: 'task' | 'phase') => void | Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  // Close on outside-click + Esc, mirroring `ColorPickerPopover`.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    // Defer the click listener one tick so the click that opened the
    // popover doesn't immediately close it.
    const t = window.setTimeout(() => {
      document.addEventListener('mousedown', onClick);
    }, 0);
    window.addEventListener('keydown', onKey);
    return () => {
      window.clearTimeout(t);
      document.removeEventListener('mousedown', onClick);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const tone =
    item.kind === 'phase'
      ? 'blue'
      : item.kind === 'milestone'
        ? 'yellow'
        : 'slate';
  const label =
    item.kind === 'phase'
      ? 'フェーズ'
      : item.kind === 'milestone'
        ? 'マイルストーン'
        : 'タスク';

  // Milestones intentionally have no toggle — render a plain badge.
  if (item.kind === 'milestone') {
    return (
      <Badge tone={tone} data-testid="kind-chip">
        {label}
      </Badge>
    );
  }

  // The toggle is binary task ⇄ phase. The chip is only "switchable"
  // when the OTHER kind passes validation; if the only alternative is
  // forbidden (e.g. nested row → phase, or phase-with-phase-children),
  // we surface the existing tooltip and disable the button.
  const otherKind: WbsItemKind = item.kind === 'phase' ? 'task' : 'phase';
  const violation = validateKindChange(item, otherKind, allItems);
  const disabled = violation !== null;
  const disabledReason = violation?.message ?? NESTED_TO_PHASE_MESSAGE;

  const onSelect = async (next: 'task' | 'phase') => {
    setOpen(false);
    if (next === item.kind) return;
    const v = validateKindChange(item, next, allItems);
    if (v) {
      window.alert(v.message);
      return;
    }
    const warning = getKindChangeWarning(item, next, allItems);
    if (warning && !window.confirm(warning.message)) return;
    try {
      await onChange(next);
    } catch (e) {
      window.alert(
        e instanceof Error ? e.message : '種別の変更に失敗しました',
      );
    }
  };

  return (
    <div ref={wrapRef} className="relative inline-block">
      <button
        type="button"
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`種別: ${label} — クリックで切り替え`}
        title={disabled ? disabledReason : '種別を切り替え'}
        onClick={() => {
          if (disabled) return;
          setOpen((v) => !v);
        }}
        data-testid="kind-chip"
        className={cn(
          // Mirror Badge styling so the visual stays a chip.
          'inline-flex items-center rounded-md px-1.5 py-0.5 text-xs font-medium',
          tone === 'blue'
            ? 'bg-blue-100 text-blue-800'
            : 'bg-slate-100 text-slate-700',
          disabled
            ? 'cursor-not-allowed opacity-60'
            : 'cursor-pointer hover:ring-1 hover:ring-slate-400',
        )}
      >
        {label}
      </button>
      {open && !disabled ? (
        <div
          role="menu"
          aria-label="種別 (task / phase)"
          data-testid="kind-chip-popover"
          className="absolute left-0 top-full z-50 mt-1 inline-flex min-w-fit overflow-hidden whitespace-nowrap rounded-md border border-slate-300 bg-white shadow-lg"
          onClick={(e) => e.stopPropagation()}
        >
          {(['task', 'phase'] as const).map((value) => {
            const isCurrent = item.kind === value;
            const optionLabel = value === 'phase' ? 'フェーズ' : 'タスク';
            return (
              <button
                key={value}
                type="button"
                role="menuitemradio"
                aria-checked={isCurrent}
                onClick={() => void onSelect(value)}
                className={cn(
                  // `whitespace-nowrap` + `flex-shrink-0` keep the
                  // option labels (`タスク` / `フェーズ`) on a single
                  // line — without these the inline-flex parent can
                  // otherwise fold them into per-character vertical
                  // strips when its computed width is unexpectedly
                  // narrow (regression observed on 2026-04-30).
                  'flex-shrink-0 whitespace-nowrap px-3 py-1 text-xs',
                  isCurrent
                    ? 'bg-slate-800 text-white'
                    : 'bg-white text-slate-700 hover:bg-slate-50',
                )}
              >
                {optionLabel}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
